import { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Stack,
    Chip,
    Alert,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Card,
    CardContent,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import WifiIcon from "@mui/icons-material/Wifi";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { format } from "date-fns";
import db from "state/db";
import { useSyncContext } from "contexts/SyncContext";


const SyncPanel = () => {
    const { sync, syncing, isOnline, pendingCount, lastResult } = useSyncContext();
    const [syncLogs, setSyncLogs] = useState([]);

    useEffect(() => {
        db.syncLog.orderBy("id").reverse().limit(20).toArray().then(setSyncLogs);
    }, [lastResult]);


    return (
        <Box sx={{ py: 2 }}>
            {/* Status */}
            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Stack 
                        direction="row" 
                        justifyContent="space-between" 
                        alignItems="center" 
                        mb={2}
                    >
                        <Typography variant="subtitle1" fontWeight={600}>
                            Sync Status
                        </Typography>
                        <Chip
                            icon={isOnline ? <WifiIcon /> : <WifiOffIcon />}
                            label={isOnline ? "Online" : "Offline"}
                            color={isOnline ? "success" : "default"}
                            size="small"
                        />
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" mb={2}>
                        <Box textAlign="center">
                            <Typography variant="h4" fontWeight={600} color="warning.main">
                                {pendingCount}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Pending
                            </Typography>
                        </Box>
                        {lastResult && (
                            <>
                                <Box textAlign="center">
                                    <Typography variant="h4" fontWeight={600} color="success.main">
                                        {lastResult.synced}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Last Synced
                                    </Typography>
                                </Box>
                                <Box textAlign="center">
                                    <Typography variant="h4" fontWeight={600} color="error.main">
                                        {lastResult.failed}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Failed
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Stack>

                    {!isOnline && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            You are offline. Orders will sync automatically when you reconnect.
                        </Alert>
                    )}

                    <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                        onClick={sync}
                        disabled={!isOnline || syncing || pendingCount === 0}
                    >
                        {syncing ? "Syncing…" : `Sync Now (${pendingCount} pending)`}
                    </Button>
                </CardContent>
            </Card>

            {/* Sync log */}
            {syncLogs.length > 0 && (
                <>
                    <Typography variant="subtitle2" color="text.secondary" mb={1}>
                        Recent History
                    </Typography>
                    <List dense>
                        {syncLogs.map((log) => (
                            <ListItem key={log.id} sx={{ px: 0 }}>
                                <ListItemText
                                    primary={
                                        <Stack direction="row" alignItems="center" gap={1}>
                                            {log.status === "success" ? (
                                                <CheckCircleIcon 
                                                    fontSize="small" 
                                                    color="success" 
                                                />
                                            ) : (
                                                <ErrorIcon 
                                                    fontSize="small" 
                                                    color="error" 
                                                />
                                            )}
                                            <Typography variant="body2">
                                                {log.status === "success"
                                                    ? `${log.synced} synced, ${log.failed} failed`
                                                    : `Error: ${log.error}`}
                                            </Typography>
                                        </Stack>
                                    }
                                    secondary={
                                        <Typography variant="caption" color="text.secondary">
                                            {log.attemptedAt ? format(new Date(log.attemptedAt), "HH:mm:ss dd/MM/yy") : ""}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                </>
            )}
        </Box>
    );
};

export default SyncPanel;
